import { useMemo, useRef, useState } from 'react';

import { EventListItem, EventMenuType } from './types/events.types';
import { EventStatus } from 'routes/referral/types/event-detail-api.types';

import { EventsList } from './components/EventsList';
import { FeaturedSlider } from './components/FeaturedSlider';
import { Filters } from './components/Filters';
import { FiltersSidebar } from './components/FiltersSidebar';
import { SearchInput } from './components/SearchInput';
import AnimatedBackground from 'components/animatedBackground/AnimatedBackground';
import CircleSpinner from 'components/circleSpinner/CircleSpinner';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import Spacing from 'components/spacing/Spacing';
import { TabMenu } from 'components/tabMenu/TabMenu';

import { useCustomEvents } from './hooks/useCustomEvents';
import { useEventsList } from './hooks/useEventsList';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

import { getSelectedFiltersCount } from './utils/filters';

import { FiltersState } from './constants';
import { EventsFiltersProvider, useEventsFilters } from './context/EventsFiltersContext';
import styles from './EventsRoute.module.scss';

const getStatusFilterKey = (status: EventStatus): string | null => {
  switch (status) {
    case EventStatus.UPCOMING:
      return 'UPCOMING';
    case EventStatus.REGISTRATION_OPEN:
      return 'REGISTRATION_OPENS';
    case EventStatus.ONGOING:
      return 'ONGOING';
    case EventStatus.REWARDS_DISTRIBUTED:
      return 'REWARD_DISTRIBUTION';
    case EventStatus.FINISHED:
      return 'FINISHED';
    default:
      return null;
  }
};

const EventsRouteContent = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { filters, setFilters } = useEventsFilters();
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const isFinishedTab = filters.eventType === EventMenuType.FINISHED;

  const activeMenu: EventMenuType = Object.values(EventMenuType).includes(filters.eventType as EventMenuType)
    ? (filters.eventType as EventMenuType)
    : EventMenuType.ALL;

  const filtersWithMenuType = useMemo(() => {
    if (isFinishedTab) {
      const finishedFilters: FiltersState = {
        ...filters,
        eventType: EventMenuType.ALL,
        status: {
          ALL: false,
          UPCOMING: false,
          REGISTRATION_OPENS: false,
          ONGOING: false,
          REWARD_DISTRIBUTION: false,
          FINISHED: true,
        },
      };
      return finishedFilters;
    }
    return { ...filters, eventType: activeMenu };
  }, [filters, activeMenu, isFinishedTab]);

  const {
    events: apiEvents,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useEventsList(filtersWithMenuType);
  const customEvents = useCustomEvents();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const events = useMemo(() => {
    const statusFilters = isFinishedTab ? filtersWithMenuType.status : filters.status;

    const filterCustom = (list: EventListItem[]) =>
      list.filter((ce) => {
        // Apply status filter
        if (!statusFilters.ALL) {
          const statusKey = getStatusFilterKey(ce.status);
          if (statusKey && !statusFilters[statusKey]) {
            return false;
          }
        }

        // Apply search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          if (!ce.name.toLowerCase().includes(searchLower)) {
            return false;
          }
        }

        return true;
      });

    // Custom events (WOF, Daily Rewards) only appear on All + Finished tabs
    if (activeMenu !== EventMenuType.ALL && activeMenu !== EventMenuType.FINISHED) {
      return apiEvents;
    }

    return [...filterCustom(customEvents), ...apiEvents];
  }, [apiEvents, customEvents, filters, filtersWithMenuType, isFinishedTab, activeMenu]);

  const selectedFiltersCount = useMemo(() => getSelectedFiltersCount(filters), [filters]);

  const onMenuClick = (menu: EventMenuType) => {
    setFilters((prev) => ({ ...prev, eventType: menu }));
  };

  const onSearchChange = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
  };

  const menuItems = [
    { title: 'All', type: EventMenuType.ALL },
    { title: 'Referral', type: EventMenuType.REFERRAL },
    { title: 'Trading', type: EventMenuType.TRADING },
    { title: 'AMA', type: EventMenuType.AMA },
    { title: 'Finished', shortTitle: 'Finished', type: EventMenuType.FINISHED },
  ];

  useInfiniteScroll({
    loadMoreRef,
    hasNextPage: hasNextPage || false,
    isFetchingNextPage: isFetchingNextPage || false,
    fetchNextPage,
  });

  return (
    <Page
      withScrollbar
      className={`tw-overflow-x-hidden tw-bg-[#101010] tw-text-center ${styles.eventsRoute}`}
      pageBackground="dark"
      loading={isLoading}
      loaderVariant="circular"
      requireTermsOfServiceConsents
    >
      <AnimatedBackground />
      <PageWrapper variant="events" homeFooter={false} landingFooter>
        <FiltersSidebar
          onClose={() => setIsSidebarOpen(false)}
          excludeRefs={[filtersButtonRef]}
          isOpen={isSidebarOpen}
        />
        <div className="tw-mx-auto tw-mt-[26px] tw-w-full tw-max-w-[1600px] tw-px-4 md:tw-px-6 lg:tw-mb-[100px] lg:tw-mt-[50px] lg:tw-px-8">
          <h1 className="tw-font-jost tw-text-[36px] tw-font-bold tw-uppercase tw-leading-[44px] tw-text-white lg:tw-text-left lg:tw-text-[32px] lg:tw-leading-[89.2px]">
            Event page
          </h1>
          <Spacing height={0} mobileHeight={30} />
          <FeaturedSlider />
          <Spacing height={30} mobileHeight={50} />
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-4 lg:tw-flex-row">
            <TabMenu<EventMenuType> items={menuItems} activeTab={activeMenu} onTabClick={onMenuClick} />
            <div className="tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-4 lg:tw-max-w-[480px]">
              <SearchInput value={filters.search || ''} onChange={onSearchChange} />
              <Filters
                ref={filtersButtonRef}
                onClick={() => setIsSidebarOpen(true)}
                selectedFiltersCount={selectedFiltersCount}
              />
            </div>
          </div>
          <Spacing height={100} mobileHeight={20} />
          <EventsList events={events} columns={{ mobile: 1, tablet: 2, desktop: 4 }} />
          {hasNextPage && <div ref={loadMoreRef} className="tw-h-10" />}
          {isFetchingNextPage && (
            <div className="tw-flex tw-justify-center tw-py-8">
              <CircleSpinner size={64} />
            </div>
          )}
        </div>
      </PageWrapper>
    </Page>
  );
};

const EventsRoute = () => {
  return (
    <EventsFiltersProvider>
      <EventsRouteContent />
    </EventsFiltersProvider>
  );
};

export default EventsRoute;
